<template>
  <div class="lab-queue page-container" v-loading="loading">
    <div class="yn-biz-header">
      <span class="yn-biz-title">理化任务队列</span>
      <div class="header-actions">
        <el-button size="mini" icon="el-icon-refresh" @click="load">刷新</el-button>
      </div>
    </div>

    <el-alert
      v-if="!isAnalyst"
      type="warning"
      :closable="false"
      show-icon
      title="当前账号不是理化分析人"
      description="请使用理化中心分析人账号登录查看分配给你的任务。"
      style="margin-bottom:12px"
    />

    <div class="lq-layout">
      <div class="lq-left">
        <div class="lq-left-hd">
          <span>我的任务 ({{ items.length }})</span>
          <el-button type="text" size="mini" @click="load">刷新</el-button>
        </div>
        <div class="lq-search">
          <el-input v-model="keyword" size="mini" clearable placeholder="关键字" prefix-icon="el-icon-search" />
        </div>
        <div class="lq-task-list">
          <div
            v-for="p in filteredItems"
            :key="p.xmbh"
            class="lq-task-item"
            :class="{ active: p.xmbh === xmbh }"
            @click="selectProject(p)"
          >
            <div class="lq-task-name">{{ p.xmmc || p.xmbh }}</div>
            <div class="lq-task-meta">
              <span>{{ p.xmbh }}</span>
              <el-tag size="mini" :type="p.arranged ? 'success' : (p.need_claim ? 'warning' : 'primary')">
                {{ p.xmzt_label || p.xmzt }}
              </el-tag>
            </div>
            <div class="lq-task-sub">
              <template v-if="p.arranged">负责 {{ (p.my_items && p.my_items.length) || 0 }} 项 · {{ p.sample_count || 0 }} 样</template>
              <template v-else>{{ p.sample_count || 0 }} 样{{ p.need_claim ? ' · 待领样' : '' }}</template>
            </div>
          </div>
          <div v-if="!loading && !filteredItems.length" class="lq-empty">暂无分配给你的任务</div>
        </div>
      </div>

      <div class="lq-right">
        <template v-if="cur">
          <div class="lq-toolbar">
            <div class="lq-toolbar-left">
              <el-button
                v-if="cur.need_claim"
                type="warning"
                size="mini"
                :loading="claiming"
                @click="claimSample(cur)"
              >确认领样</el-button>
              <span class="lq-meta">
                <strong>{{ cur.xmmc }}</strong>
                <span class="lq-meta-sep">·</span>
                {{ cur.xmbh }}
              </span>
            </div>
            <div class="lq-toolbar-right">
              <el-button type="text" size="mini" @click="$router.push({ path: '/task-track', query: { xmbh: cur.xmbh } })">任务监控</el-button>
              <el-button type="text" size="mini" @click="$router.push({ path: '/soil', query: { xmbh: cur.xmbh } })">完整录入页</el-button>
            </div>
          </div>

          <div class="lq-right-body">
            <div v-if="cur.need_claim && !cur.can_enter" class="lq-block-hint">
              请先确认领样后再上传原表
            </div>

            <div class="lq-config">
              <div class="lq-config-top">
                <el-popover
                  placement="bottom-start"
                  width="360"
                  trigger="click"
                  popper-class="lq-sample-popper"
                >
                  <div class="lq-sample-bar">
                    <el-input
                      v-model="sampleKeyword"
                      size="mini"
                      clearable
                      placeholder="样品搜索"
                      prefix-icon="el-icon-search"
                      class="lq-sample-search"
                    />
                    <el-button type="text" size="mini" @click="checkAllSamples">全选</el-button>
                    <el-button type="text" size="mini" @click="clearSampleChecks">清空</el-button>
                  </div>
                  <el-tree
                    ref="sampleTree"
                    :data="sampleTreeData"
                    show-checkbox
                    node-key="id"
                    :props="{ label: 'label', children: 'children' }"
                    :filter-node-method="filterSampleNode"
                    default-expand-all
                    class="lq-sample-tree"
                    @check="onSampleCheck"
                  />
                  <div v-if="!sampleTreeData.length" class="lq-empty">当前任务暂无样品编码</div>
                  <button slot="reference" type="button" class="lq-sample-trigger">
                    <span class="lq-sample-trigger-label">样品勾选</span>
                    <span class="lq-meta-chip">已选 {{ checkedSampleCodes.length }}</span>
                    <svg class="lq-sample-trigger-ico" viewBox="0 0 12 12" aria-hidden="true">
                      <path fill="currentColor" d="M2.5 4.5L6 8l3.5-3.5H2.5z"/>
                    </svg>
                  </button>
                </el-popover>
              </div>

              <div class="lq-select-row">
                <div class="lq-field">
                  <label class="lq-field-label">CMA 能力项（土壤）</label>
                  <el-select
                    v-model="selectedCmaIds"
                    v-loading="cmaLoading"
                    multiple
                    filterable
                    collapse-tags
                    clearable
                    size="small"
                    class="lq-multi-select"
                    placeholder="搜索并多选参数 / 标准"
                    :disabled="!canOperate || cmaSaving"
                    @change="onCmaSelectChange"
                  >
                    <el-option
                      v-for="row in cmaSoilItems"
                      :key="row.id"
                      :label="cmaOptionLabel(row)"
                      :value="String(row.id)"
                    >
                      <span class="lq-opt-main">{{ row.param_name || '—' }}</span>
                      <span class="lq-opt-sub">{{ row.std_code || '' }} {{ row.std_name || '' }}</span>
                    </el-option>
                  </el-select>
                  <p v-if="!cmaReady" class="lq-field-tip is-warn">请至少选择一项</p>
                  <p v-else class="lq-field-tip">已选 {{ selectedCmaIds.length }} 项</p>
                </div>
                <div class="lq-field">
                  <label class="lq-field-label">检测原表</label>
                  <el-select
                    v-model="selectedMethods"
                    multiple
                    filterable
                    collapse-tags
                    clearable
                    size="small"
                    class="lq-multi-select"
                    placeholder="请先选 CMA，再多选原表"
                    :disabled="!canOperate || !cmaReady"
                    @change="onMethodsChange"
                  >
                    <el-option
                      v-for="f in labForms"
                      :key="f.num"
                      :label="'GSYN-' + f.num + ' ' + (f.mc || '')"
                      :value="String(f.num)"
                    >
                      <span class="lq-opt-main">GSYN-{{ f.num }}</span>
                      <span class="lq-opt-sub">{{ f.mc }}</span>
                    </el-option>
                  </el-select>
                  <p v-if="!cmaReady" class="lq-field-tip is-warn">先完成 CMA</p>
                  <p v-else class="lq-field-tip">已选 {{ selectedMethods.length }} 张</p>
                </div>
              </div>
            </div>

            <div ref="docxPanelAnchor" class="lq-docx-block">
              <LabOfflineDocxPanel
                :xmbh="cur.xmbh"
                :form-nums="cmaReady ? selectedMethods : []"
                :lab-forms="labForms"
                :can-operate="canOperate && cmaReady"
              />
            </div>
          </div>
        </template>
        <el-empty v-else description="请从左侧选择任务" />
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api'
import { isLabAnalyst } from '@/utils/user'
import LabOfflineDocxPanel from '@/components/LabOfflineDocxPanel.vue'

export default {
  name: 'LabQueue',
  components: { LabOfflineDocxPanel },
  data() {
    return {
      loading: false,
      claiming: false,
      keyword: '',
      sampleKeyword: '',
      items: [],
      xmbh: this.$route.query.xmbh || '',
      cur: null,
      labForms: [],
      selectedMethods: [],
      checkedSampleCodes: [],
      flatSamples: [],
      cmaSoilItems: [],
      cmaLoading: false,
      cmaSaving: false,
      selectedCmaIds: [],
      _cmaSaveTimer: null,
      _methodsSaveTimer: null
    }
  },
  computed: {
    isAnalyst() {
      return isLabAnalyst()
    },
    cmaReady() {
      return (this.selectedCmaIds || []).length > 0
    },
    canOperate() {
      if (!this.cur) return false
      if (this.cur.need_claim && !this.cur.can_enter) return false
      return true
    },
    filteredItems() {
      const k = String(this.keyword || '').trim()
      if (!k) return this.items
      return this.items.filter(p => {
        const blob = `${p.xmbh || ''} ${p.xmmc || ''} ${p.my_item_labels || ''}`
        return blob.indexOf(k) >= 0
      })
    },
    sampleTreeData() {
      if (!this.cur) return []
      if (this.cur.arranged && Array.isArray(this.cur.my_items) && this.cur.my_items.length) {
        return this.cur.my_items.map((it, idx) => {
          const codes = Array.isArray(it.codes) ? it.codes.map(String).filter(Boolean) : []
          const children = codes.length
            ? codes.map(code => ({
              id: `s:${it.key || idx}:${code}`,
              label: code,
              code,
              isSample: true
            }))
            : [{
              id: `s:${it.key || idx}:_empty`,
              label: '（无样品编码）',
              code: '',
              disabled: true,
              isSample: false
            }]
          return {
            id: `i:${it.key || idx}`,
            label: `${it.item || '检测项'}（${codes.length || it.count || 0}）`,
            isSample: false,
            children
          }
        })
      }
      if (this.flatSamples.length) {
        return [{
          id: 'i:all',
          label: `全部样品（${this.flatSamples.length}）`,
          isSample: false,
          children: this.flatSamples.map(s => ({
            id: `s:all:${s.ypbh || s.id}`,
            label: s.ypbh || String(s.id),
            code: s.ypbh || '',
            isSample: true
          }))
        }]
      }
      return []
    }
  },
  watch: {
    sampleKeyword(val) {
      if (this.$refs.sampleTree) this.$refs.sampleTree.filter(val)
    },
    cmaReady(val) {
      if (val && this.cur) this.loadMethods()
      else this.selectedMethods = []
    }
  },
  mounted() {
    this.loadRegistry()
    this.load()
  },
  beforeDestroy() {
    if (this._methodsSaveTimer) clearTimeout(this._methodsSaveTimer)
    if (this._cmaSaveTimer) clearTimeout(this._cmaSaveTimer)
  },
  methods: {
    cmaOptionLabel(row) {
      const name = (row && row.param_name) || '—'
      const code = (row && row.std_code) || ''
      const std = (row && row.std_name) || ''
      const limit = row && row.scope_limit ? ` · ${row.scope_limit}` : ''
      return `${name} / ${code} ${std}${limit}`.replace(/\s+/g, ' ').trim()
    },
    sortLabFormsWithContinuations(forms) {
      const list = Array.isArray(forms) ? forms.slice() : []
      const contMap = {}
      list.forEach(f => {
        const parent = String(f.continuationOf || '')
        if (!parent) return
        if (!contMap[parent]) contMap[parent] = []
        contMap[parent].push(f)
      })
      const out = []
      const used = new Set()
      list.forEach(f => {
        const num = String(f.num)
        if (used.has(num) || f.continuationOf) return
        out.push(f)
        used.add(num)
        ;(contMap[num] || []).forEach(x => {
          out.push(x)
          used.add(String(x.num))
        })
      })
      list.forEach(f => {
        const num = String(f.num)
        if (!used.has(num)) out.push(f)
      })
      return out
    },
    async loadRegistry() {
      try {
        const res = await api.get('yn_tr_lab_registry')
        if (res.data && Array.isArray(res.data.labForms)) {
          this.labForms = this.sortLabFormsWithContinuations(res.data.labForms)
        }
      } catch (e) {
        this.labForms = []
      }
    },
    async load() {
      this.loading = true
      try {
        const res = await api.get('yn_tr_lab_queue', {})
        this.items = (res.data && res.data.items) || []
        if (this.xmbh) {
          const hit = this.items.find(p => p.xmbh === this.xmbh)
          if (hit) await this.selectProject(hit)
          else if (this.items.length) await this.selectProject(this.items[0])
          else this.cur = null
        } else if (this.items.length === 1) {
          await this.selectProject(this.items[0])
        }
      } catch (e) {
        this.items = []
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    async selectProject(p) {
      if (!p || !p.xmbh) return
      this.cur = p
      this.xmbh = p.xmbh
      this.checkedSampleCodes = []
      this.sampleKeyword = ''
      this.selectedMethods = []
      this.selectedCmaIds = []
      this.flatSamples = []
      if (this.$route.query.xmbh !== p.xmbh) {
        this.$router.replace({ path: '/soil/lab-queue', query: { xmbh: p.xmbh } }).catch(() => {})
      }
      await Promise.all([
        this.loadCmaCatalog(),
        this.loadProjectCma(),
        this.loadFlatSamplesIfNeeded()
      ])
      await this.loadMethods()
      this.$nextTick(() => {
        this.checkAllSamples()
      })
    },
    async loadProjectCma() {
      if (!this.cur) return
      try {
        const res = await api.get('yn_tr_project_cma', { xmbh: this.cur.xmbh })
        const d = res.data || {}
        this.selectedCmaIds = (d.ids || []).map(String)
      } catch (e) {
        this.selectedCmaIds = []
      }
    },
    async loadCmaCatalog() {
      this.cmaLoading = true
      try {
        const res = await api.get('yn_cma_scope_list', {
          major: '三、土壤和水系沉积物',
          page: 1,
          page_size: 200
        })
        this.cmaSoilItems = (res.data && res.data.items) || []
      } catch (e) {
        this.cmaSoilItems = []
        this.$message.error('加载土壤 CMA 能力项失败')
      } finally {
        this.cmaLoading = false
      }
    },
    onCmaSelectChange(ids) {
      this.selectedCmaIds = (ids || []).map(String)
      if (!this.cmaReady) this.selectedMethods = []
      if (this._cmaSaveTimer) clearTimeout(this._cmaSaveTimer)
      this._cmaSaveTimer = setTimeout(() => {
        if (this.cur) this.saveProjectCma({ silent: true })
      }, 500)
    },
    async saveProjectCma(opts = {}) {
      const silent = !!opts.silent
      if (!this.cur) return
      this.cmaSaving = true
      try {
        const res = await api.post('yn_tr_project_cma', {
          xmbh: this.cur.xmbh,
          ids: JSON.stringify(this.selectedCmaIds.map(id => parseInt(id, 10)).filter(n => n > 0))
        })
        const d = res.data || {}
        if (d.error) {
          if (!silent) this.$message.error(d.error)
          return
        }
        this.selectedCmaIds = (d.ids || []).map(String)
        if (!silent) this.$message.success('CMA 能力项已保存')
      } catch (e) {
        if (!silent) this.$message.error('保存 CMA 能力项失败')
      } finally {
        this.cmaSaving = false
      }
    },
    async loadFlatSamplesIfNeeded() {
      if (!this.cur) return
      if (this.cur.arranged && Array.isArray(this.cur.my_items) && this.cur.my_items.length) {
        this.flatSamples = []
        return
      }
      try {
        const res = await api.get('yn_tr_samples', { xmbh: this.cur.xmbh })
        const d = res.data || {}
        const list = Array.isArray(d) ? d : (d.samples || [])
        this.flatSamples = list
      } catch (e) {
        this.flatSamples = []
      }
    },
    async loadMethods() {
      if (!this.cur) return
      try {
        const res = await api.get('yn_tr_methods', { xmbh: this.cur.xmbh })
        const list = (res.data && res.data.methods) || []
        if (this.cmaReady) {
          this.selectedMethods = list.filter(m => m.enabled == 1).map(m => String(m.form_num))
        } else {
          this.selectedMethods = []
        }
      } catch (e) {
        this.selectedMethods = []
      }
    },
    onMethodsChange() {
      if (!this.cmaReady) {
        this.selectedMethods = []
        return
      }
      if (this._methodsSaveTimer) clearTimeout(this._methodsSaveTimer)
      this._methodsSaveTimer = setTimeout(() => {
        this.saveMethods({ silent: true })
      }, 600)
    },
    async saveMethods(opts = {}) {
      const silent = !!opts.silent
      if (!this.cur) return
      const methods = Array.from(new Set((this.selectedMethods || []).map(String)))
      try {
        const res = await api.post('yn_tr_methods', {
          xmbh: this.cur.xmbh,
          methods: JSON.stringify(methods)
        })
        if (res.data && res.data.ok) {
          if (!silent) this.$message.success('原表已保存')
          const list = res.data.methods || []
          const got = list.filter(m => m.enabled == 1).map(m => String(m.form_num))
          const merged = got.slice()
          methods.forEach(n => {
            if (/^\d+-\d+$/.test(n) && merged.indexOf(n) < 0) merged.push(n)
          })
          this.selectedMethods = merged
        } else if (!silent) {
          this.$message.error('保存失败')
        }
      } catch (e) {
        if (!silent) this.$message.error('保存失败')
      }
    },
    filterSampleNode(value, data) {
      if (!value) return true
      return String(data.label || '').indexOf(value) >= 0
    },
    onSampleCheck() {
      const tree = this.$refs.sampleTree
      if (!tree) return
      const nodes = tree.getCheckedNodes(true, false) || []
      const codes = []
      nodes.forEach(n => {
        if (n && n.isSample && n.code) codes.push(String(n.code))
      })
      this.checkedSampleCodes = Array.from(new Set(codes))
    },
    checkAllSamples() {
      const tree = this.$refs.sampleTree
      if (!tree) return
      const keys = []
      const walk = (nodes) => {
        ;(nodes || []).forEach(n => {
          if (n.isSample && n.code && !n.disabled) keys.push(n.id)
          if (n.children) walk(n.children)
        })
      }
      walk(this.sampleTreeData)
      tree.setCheckedKeys(keys)
      this.onSampleCheck()
    },
    clearSampleChecks() {
      const tree = this.$refs.sampleTree
      if (!tree) return
      tree.setCheckedKeys([])
      this.checkedSampleCodes = []
    },
    async claimSample(item) {
      if (!item) return
      this.claiming = true
      try {
        const res = await api.post('yn_tr_lab_claim', { xmbh: item.xmbh })
        if (res.data && res.data.error) {
          this.$message.error(res.data.error)
          return
        }
        this.$message.success('已领样')
        await this.load()
      } catch (e) {
        this.$message.error('领样失败')
      } finally {
        this.claiming = false
      }
    }
  }
}
</script>

<style scoped>
.lq-layout {
  display: flex;
  border: 1px solid #e4e7ed;
  background: #fff;
  min-height: calc(100vh - 140px);
}
.lq-left {
  width: 280px;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}
.lq-left-hd {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e4e7ed;
  font-weight: 600;
  font-size: 13px;
}
.lq-search { padding: 8px 12px; border-bottom: 1px solid #e4e7ed; }
.lq-task-list { flex: 1; overflow: auto; }
.lq-task-item {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f2f5;
  cursor: pointer;
}
.lq-task-item:hover { background: #f5f7fa; }
.lq-task-item.active { background: #ecf5ff; }
.lq-task-name { font-weight: 600; font-size: 13px; color: #303133; }
.lq-task-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.lq-task-sub { margin-top: 2px; font-size: 12px; color: #f56c6c; }
.lq-empty { color: #909399; font-size: 12px; padding: 16px; text-align: center; }
.lq-right { flex: 1; min-width: 0; display: flex; flex-direction: column; overflow: hidden; }
.lq-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid #e4e7ed;
  background: #f7f8fa;
}
.lq-toolbar-left,
.lq-toolbar-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.lq-meta {
  font-size: 13px;
  color: #646a73;
  margin-left: 4px;
}
.lq-meta strong {
  color: #1f2329;
  font-weight: 600;
}
.lq-meta-sep {
  margin: 0 4px;
  color: #c0c4cc;
}
.lq-meta-chip {
  display: inline-block;
  margin-left: 0;
  padding: 1px 8px;
  border-radius: 10px;
  background: #e8f3ff;
  color: #3370ff;
  font-size: 12px;
  font-weight: 400;
}
.lq-right-body {
  flex: 1;
  overflow: auto;
  padding: 14px;
  background: #f3f4f6;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.lq-block-hint {
  padding: 8px 12px;
  background: #fff6e6;
  color: #b88230;
  font-size: 12px;
  border-radius: 6px;
}
.lq-config {
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 8px;
  padding: 12px 14px 14px;
}
.lq-config-top {
  margin-bottom: 12px;
}
.lq-sample-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  color: #1f2329;
}
.lq-sample-trigger:hover {
  border-color: #3370ff;
  color: #3370ff;
}
.lq-sample-trigger-label {
  font-weight: 600;
}
.lq-sample-trigger-ico {
  width: 12px;
  height: 12px;
  color: #8a919f;
}
.lq-sample-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.lq-sample-search {
  flex: 1;
}
.lq-sample-tree {
  max-height: 260px;
  overflow: auto;
}
.lq-select-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
@media (max-width: 1100px) {
  .lq-select-row {
    grid-template-columns: 1fr;
  }
}
.lq-field-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #1f2329;
  margin-bottom: 6px;
}
.lq-multi-select {
  width: 100%;
}
.lq-multi-select >>> .el-select__tags {
  max-width: calc(100% - 30px);
}
.lq-field-tip {
  margin: 6px 0 0;
  font-size: 12px;
  color: #8a919f;
}
.lq-field-tip.is-warn {
  color: #b88230;
}
.lq-opt-main {
  float: left;
  font-size: 13px;
  color: #1f2329;
}
.lq-opt-sub {
  float: right;
  margin-left: 12px;
  max-width: 55%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: #8a919f;
}
.lq-docx-block {
  flex: 1;
  min-height: 280px;
}
</style>

<style>
.lq-sample-popper {
  padding: 10px 12px !important;
}
.lq-sample-popper .lq-meta-chip {
  margin-left: 0;
}
</style>
